import {
  UploadFormOption,
  UploadFormOptionCheckbox,
  UploadFormOptionCheckboxOptions,
  UploadFormOptionDate,
  UploadFormOptionDropdown,
  UploadFormOptionDropdownOptions,
  UploadFormOptionNumberOfDays
} from '../../UploadForm.types';
import { NudosDropdownWithSearchbar, NudosSelectButton } from 'components/NudosComponents';
import { DatePicker, InputNumber } from 'antd';
import './UploadOptions.scss';

interface UploadOptionsProps {
  options: UploadFormOption[];
  showSeparator?: boolean;
}

const UploadOptions = ({ options, showSeparator = true }: UploadOptionsProps) => {
  const renderCheckboxOption = (option: UploadFormOptionCheckboxOptions) => (
    <div className="uploadOptionsOption" key={option.label}>
      <NudosSelectButton
        isButtonActive={option.checked}
        handleClickButton={option.handleChange}
        customClassName="uploadOptionsCheckbox"
        isButtonDisabled={option.disabled}
      />
      <span className="uploadOptionsLabel">{option.label}</span>
    </div>
  );

  const renderCheckboxOptions = (input: UploadFormOptionCheckbox) => {
    return input.options && input.options.map(option => renderCheckboxOption(option));
  };

  const renderDropdownOptions = (options: UploadFormOptionDropdownOptions[], clickOptionCallback?: (option: UploadFormOptionDropdownOptions) => void) => {
    const handleClickOption = (option: UploadFormOptionDropdownOptions) => {
      clickOptionCallback?.(option);
      option.handleChange?.();
    };
    return (
      options?.map(opt =>
        opt.element ? (
          <div key={`${opt.label}-${opt.value}`} onClick={() => handleClickOption(opt)}>
            {opt.element}
          </div>
        ) : (
          <option
            key={`${opt.label}-${opt.value}`}
            value={opt.value}
            onClick={() => handleClickOption(opt)}
            className="selectOption"
          >
            {opt.label}
          </option>
        )
      ) || []
    );
  };

  const renderDropdownOption = (input: UploadFormOptionDropdown) => {
    const rows = input.options || [];
    const currentValue = input.defaultOption || { label: '', value: '' };
    const formDropdownOptionsCallback = (rawOptions: UploadFormOptionDropdownOptions[], clickOptionCallback: (option: UploadFormOptionDropdownOptions) => void): JSX.Element[] => {
      return renderDropdownOptions(rawOptions, clickOptionCallback);
    };
    const filterBySearchStringCallback = (search: string, rawOptions: UploadFormOptionDropdownOptions[]) => {
      const filteredOptions = rawOptions.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()));
      return filteredOptions;
    };

    return (
      <NudosDropdownWithSearchbar
        key={input.label}
        rawOptions={rows}
        currentValue={currentValue}
        isFilled={input.defaultOption?.isFilled || false}
        isDeactivated={false}
        closesOnChangeSelection
        hideErrorText={false}
        customClassname="uploadOptionsSelect"
        formDropdownOptionsCallback={formDropdownOptionsCallback}
        filterBySearchStringCallback={filterBySearchStringCallback}
      />
    );
  };

  const renderDateOption = (input: UploadFormOptionDate) => {
    return (
      <DatePicker
        className={`uploadOptionsDate ${input.isFilled ? 'uploadOptionsFilled' : ''}`}
        onChange={value => input.handleChange?.(value?.toDate())}
        size="large"
        placeholder={input.placeholder || ''}
      />
    );
  };

  const renderNumberOfDaysOption = (input: UploadFormOptionNumberOfDays) => {
    return (
      <InputNumber
        className={`uploadOptionsNumberOfDays ${input.isFilled ? 'uploadOptionsFilled' : ''}`}
        onChange={value => input.handleChange?.(+value)}
        size="large"
        placeholder={input.placeholder || '15 días'}
        formatter={input.value && `${input.value}`.length > 1 ? value => `${value} días` : undefined}
        parser={value => value!.replace('días', '')}
        value={input.value ? `${input.value}` as string : undefined}
      />
    );
  };

  const renderOption = (input: UploadFormOption) => {
    const renderType = {
      checkbox: renderCheckboxOptions(input as UploadFormOptionCheckbox),
      dropdown: renderDropdownOption(input as UploadFormOptionDropdown),
      date: renderDateOption(input as UploadFormOptionDate),
      'number-of-days': renderNumberOfDaysOption(input as UploadFormOptionNumberOfDays)
    };
    return renderType[input.type];
  };

  return (
    <section className="uploadOptions">
      {showSeparator && <hr />}
      {options &&
        options.map(option => (
          <div className="uploadOptionsGroup" key={option.label}>
            {option.label && <p className="uploadOptionsText">{option.label}</p>}
            <div className="uploadOptionsOptions">{renderOption(option)}</div>
          </div>
        ))}
      {showSeparator && <hr />}
    </section>
  );
};

export default UploadOptions;
